<template>
  <NoticeBase
    :notice="notice"
    :relative-time="relativeTime"
    notice-class="inbox-set-relation-notice"
    @open="open"
    @archived="$emit('archived', $event)"
  >
    <span class="issuer">
      <span class="avatar" :class="`color-${avatarColorIndex}`">
        <span class="avatar-text">{{ avatarText }}</span>
      </span>
      <span class="issuer-name">{{ issuerName }}</span>
    </span>
    <span class="text">
      {{
        propertyName ? `set you as ${propertyName}` : "added you as a relation"
      }}
      {{ objectTitle ? "in" : "" }}
    </span>
    <span v-if="objectTitle" class="title">{{ objectTitle }}</span>
  </NoticeBase>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, shallowRef, toRef } from "vue";

import {
  getNoticeObjectTitle,
  getPropertyNameFromPath,
  getTriggeredName,
  openNotice,
  useNoticeRelativeTime,
  type SetRelationNotice,
} from "@/components/inbox/notice";
import NoticeBase from "@/components/inbox/NoticeBase.vue";
import * as inject from "@/components/inject";

export default defineComponent({
  name: "InboxAssignmentNotice",
  components: { NoticeBase },
  props: {
    notice: {
      type: Object as PropType<SetRelationNotice>,
      required: true,
    },
  },
  emits: {
    archived: (_noticeId: string) => true,
  },
  setup(props) {
    const controller = inject.controller();
    const profile = inject.profile();
    const logger = inject.logger();
    const router = inject.router();

    const noticeRef = toRef(props, "notice");

    const objectTitle = shallowRef<string | null>(null);
    const propertyName = shallowRef<string | null>(null);

    const issuerName = computed(() =>
      getTriggeredName(props.notice, profile.value)
    );

    const avatarText = computed(() => {
      const text = issuerName.value.trim();
      if (!text) return "?";
      return text.charAt(0).toUpperCase();
    });

    const avatarColorIndex = computed(() => {
      const ch = avatarText.value.charAt(0) || "?";
      return ch.charCodeAt(0) % 8;
    });

    const relativeTime = useNoticeRelativeTime(noticeRef);

    getNoticeObjectTitle(props.notice.origin.object, controller).then(
      (title) => {
        objectTitle.value = title;
      }
    );
    getPropertyNameFromPath(
      props.notice.origin.object,
      props.notice.origin.path,
      controller
    ).then((name) => {
      propertyName.value = name;
    });

    const open = async () => {
      await openNotice(controller, router, logger, props.notice);
    };

    return {
      avatarColorIndex,
      avatarText,
      open,
      issuerName,
      objectTitle,
      relativeTime,
      propertyName,
    };
  },
});
</script>

<style scoped lang="scss">
.issuer {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  vertical-align: bottom;
}

.avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  flex: 0 0 18px;
  margin-inline-end: 5px;

  .avatar-text {
    font-size: 11px;
    line-height: 11px;
    font-weight: 600;
    @include themed() {
      color: t($bg);
    }
  }
}

// Keep colors consistent with Mention.vue / Avatar.vue fallbacks
.avatar.color-0 {
  background-color: $calendar-a-50;
}
.avatar.color-1 {
  background-color: $calendar-b-50;
}
.avatar.color-2 {
  background-color: $calendar-c-50;
}
.avatar.color-3 {
  background-color: $calendar-d-50;
}
.avatar.color-4 {
  background-color: $calendar-e-50;
}
.avatar.color-5 {
  background-color: $calendar-f-50;
}
.avatar.color-6 {
  background-color: $calendar-g-50;
}
.avatar.color-7 {
  background-color: $calendar-h-50;
}

.text {
  margin-inline-start: 6px;
  margin-inline-end: 6px;
  @include themed() {
    color: t($grey-60);
  }
}
</style>
